import { Dispatch, FC, SetStateAction } from 'react'
import { Formik, Field, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Button from 'react-bootstrap/Button'

import { NextData } from 'components/features/accountcreation/NewAccountMain'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  confirmPasswordValidation,
  createPasswordValidation,
  getPasswordRulesPopover
} from 'share/utils/passwordUtils'
import {
  CreatePasswordVariables,
  CreatePasswordResponseData
} from 'share/mutations/accountcreation'

import './styles.scss'

interface NewAccountCreateYourPasswordFormProps {
  confirmPasswordValidationResult: ConfirmPasswordValidationType
  createPasswordHelper(
    createPasswordVariables: CreatePasswordVariables
  ): Promise<CreatePasswordResponseData>
  createPasswordValidationResult: CreatePasswordValidationType
  invitationId: string
  nextStep(data: NextData): void
  placement: 'right' | 'top'
  setConfirmPasswordValidationResult: Dispatch<
    SetStateAction<ConfirmPasswordValidationType>
  >
  setCreatePasswordValidationResult: Dispatch<
    SetStateAction<CreatePasswordValidationType>
  >
  userId: string
  emailAddress: string
  verifyPhone: boolean
  verifyEmail: boolean
}

interface Values {
  emailAddress: string
  createPassword: string
  confirmPassword: string
  acceptTerms: boolean
}

const validationSchema = Yup.object({
  createPassword: Yup.string().required(''),
  acceptTerms: Yup.bool().oneOf([true], 'Accept Terms & Conditions is required')
})

const STRETTO_LEGAL_POLICIES_URL = 'https://www.stretto.com/legal-policies/'

const NEW_ACCOUNT_CREATE_TERMS_URL = `${STRETTO_LEGAL_POLICIES_URL}`

const NEW_ACCOUNT_CREATE_PRIVACY_URL = `${STRETTO_LEGAL_POLICIES_URL}`

const NewAccountCreateYourPasswordForm: FC<NewAccountCreateYourPasswordFormProps> =
  ({
    confirmPasswordValidationResult,
    createPasswordHelper,
    createPasswordValidationResult,
    invitationId,
    nextStep,
    placement,
    setConfirmPasswordValidationResult,
    setCreatePasswordValidationResult,
    userId,
    emailAddress,
    verifyPhone,
    verifyEmail
  }) => {
    const initialValues = {
      emailAddress: emailAddress,
      createPassword: '',
      confirmPassword: '',
      acceptTerms: false
    }
    const popover = getPasswordRulesPopover(createPasswordValidationResult)

    function handleCreatePasswordChange(password: string) {
      const result = createPasswordValidation(password)
      setCreatePasswordValidationResult(result)
      if (confirmPasswordValidationResult.password.length > 0) {
        const resultConfirm = confirmPasswordValidation(
          confirmPasswordValidationResult.password,
          password
        )
        setConfirmPasswordValidationResult(resultConfirm)
      }
    }

    function handleConfirmPasswordChange(password: string) {
      const result = confirmPasswordValidation(
        password,
        createPasswordValidationResult.password
      )
      if (createPasswordValidationResult.password !== password) {
        result.errorMessage = 'Your passwords do not match.'
      }
      setConfirmPasswordValidationResult(result)
    }

    return (
      <>
        <Formik
          initialValues={initialValues}
          onSubmit={async (
            values: Values,
            { setSubmitting }: FormikHelpers<Values>
          ) => {
            if (
              createPasswordValidationResult.isValid &&
              confirmPasswordValidationResult.isValid
            ) {
              // call api
              const input: CreatePasswordVariables = {
                signup: {
                  userName: userId,
                  password: createPasswordValidationResult.password,
                  invitationGuid: invitationId
                }
              }
              const createPasswordResult = await createPasswordHelper(input)
              if (createPasswordResult.success) {
                nextStep({
                  userId: userId,
                  password: createPasswordValidationResult.password,
                  mobilePhone: createPasswordResult.mobilePhone,
                  verifyPhone: verifyPhone,
                  verifyEmail: verifyEmail
                })
              } else {
                setConfirmPasswordValidationResult({
                  ...confirmPasswordValidationResult,
                  errorMessage: createPasswordResult.error
                })
              }
            } else {
              setSubmitting(false)
              let errorMsg = 'This field is required.'
              if (confirmPasswordValidationResult.password.length > 0) {
                errorMsg = 'Your passwords do not match.'
              }
              const confirmStyle = 'form-control is-invalid'

              setConfirmPasswordValidationResult({
                ...confirmPasswordValidationResult,
                errorMessage: errorMsg,
                confirmPasswordStyle: confirmStyle
              })
            }
          }}
          validationSchema={validationSchema}
        >
          {(formik) => {
            return (
              <Form className='form-create-pwd'>
                <div className='row'>
                  <div className='form-group col-12 col-sm-12 col-md'>
                    <label htmlFor='emailAddress'>
                      Email Address (User ID)
                    </label>
                    <Field
                      readOnly={true}
                      type='email'
                      id='emailAddress'
                      name='emailAddress'
                      data-testid='create-account-verify-contact-info-email-address-field'
                      placeholder=''
                      className='form-control'
                    />
                  </div>
                  <div className='form-group col-12'>
                    <label htmlFor='createPassword'>Create Password</label>
                    <OverlayTrigger
                      trigger='focus'
                      defaultShow={true}
                      placement={placement}
                      overlay={popover}
                    >
                      <Field
                        type='password'
                        id='createPassword'
                        name='createPassword'
                        data-testid='create-account-password-createPassword'
                        className={
                          createPasswordValidationResult.createPasswordStyle
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          formik.handleChange(e)
                          handleCreatePasswordChange(e.target.value)
                        }}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleCreatePasswordChange(e.target.value)
                        }}
                        autoFocus
                      />
                    </OverlayTrigger>
                    {/* {createPasswordValidationResult.errorMessages.map(
                  (errorMsg) => (
                    <div className='text-danger' key={errorMsg}>
                      {errorMsg}
                    </div>
                  )
                )} */}
                  </div>
                </div>

                <div className='row'>
                  <div className='form-group col-12'>
                    <label htmlFor='confirmPassword'>Confirm Password</label>
                    <Field
                      type='password'
                      id='confirmPassword'
                      name='confirmPassword'
                      data-testid='create-account-password-confirmPassword'
                      className={
                        confirmPasswordValidationResult.confirmPasswordStyle
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        formik.handleChange(e)
                        handleConfirmPasswordChange(e.target.value)
                      }}
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                        handleConfirmPasswordChange(e.target.value)
                      }}
                      placeholder=''
                      disabled={!createPasswordValidationResult.isValid}
                    />
                    <div
                      data-testid='create-account-password-errorText'
                      className='text-danger'
                    >
                      {confirmPasswordValidationResult.errorMessage}
                    </div>
                  </div>
                </div>

                <div className='row'>
                  <div className='col-12 text-left'>
                    <label id='acceptTerms' htmlFor='acceptTerms' />
                    <Field
                      type='checkbox'
                      id='acceptTerms'
                      name='acceptTerms'
                      data-testid='create-account-password-acceptTerms'
                      className='check-terms'
                      placeholder=''
                    />
                    <span className='ml-1'>
                      I have viewed and agree to Stretto's{' '}
                      <a
                        href={NEW_ACCOUNT_CREATE_TERMS_URL}
                        rel='noopener noreferrer'
                        target='_blank'
                        data-testid='create-account-password-terms-link'
                      >
                        Terms
                      </a>{' '}
                      &{' '}
                      <a
                        href={NEW_ACCOUNT_CREATE_PRIVACY_URL}
                        rel='noopener noreferrer'
                        target='_blank'
                        data-testid='create-account-password-privacy-link'
                      >
                        Privacy
                      </a>
                      .
                    </span>
                    {formik.touched.acceptTerms && formik.errors.acceptTerms ? (
                      <div
                        className='text-danger'
                        data-testid='create-account-password-other-eror-text'
                      >
                        {formik.errors.acceptTerms}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className='row'>
                  <div className='col-12 btn-container'>
                    <Button
                      type='submit'
                      name='submit'
                      data-testid='create-account-password-next-button'
                      className='btn-submit'
                      disabled={
                        formik.isSubmitting ||
                        !createPasswordValidationResult.isValid ||
                        !confirmPasswordValidationResult.isValid ||
                        !formik.values.acceptTerms
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </>
    )
  }

export default NewAccountCreateYourPasswordForm
